import { useCallback, useEffect, useRef, useState } from "react";
import { subscribeToIntegrationOauthCompletion } from "./completion-channel.js";
import type { AuthorizationWindow } from "../settings/oauth-window.js";

/**
 * PB-1592 — how the console notices that an authorization it started has
 * finished in another tab.
 *
 * The signal is the fast path and the poll is the honest one. A
 * `BroadcastChannel` message arrives in milliseconds when everything works, and
 * three things stop it working that this hook cannot detect and must survive
 * anyway: the API is absent in some embedded webviews, a message posted while
 * this page was being torn down and re-mounted is simply gone, and a user who
 * abandons the flow by closing the tab never produces one at all. So the poll
 * runs regardless, and the signal's job is to END it early rather than to be
 * the only way home.
 *
 * IT IS BOUNDED, AND ONLY RUNS DURING AN ATTEMPT. Polling starts on a Connect
 * click and stops at the first of: the signal, the authorization tab closing,
 * or the tick ceiling. Nothing here runs on an idle Settings page — a console
 * that quietly refetched for as long as it was open would be a background load
 * multiplied by every user who left a tab in it.
 */

/** ~3s between asks, ceiling ~2 minutes — long enough for a real consent screen
    with an MFA prompt in it, short enough that an abandoned attempt costs a
    bounded number of requests rather than an open-ended stream. */
export const OAUTH_COMPLETION_POLL_INTERVAL_MS = 3_000;
export const OAUTH_COMPLETION_POLL_MAX_TICKS = 40;

export interface OauthCompletionWatcher {
  /** True while an attempt is being watched. Drives the console's own hint. */
  readonly watching: boolean;
  /** Begin watching an attempt. Replaces any attempt already being watched. */
  watch: (authorizationWindow: AuthorizationWindow) => void;
  /** Give up on the current attempt without refetching. */
  stop: () => void;
}

/**
 * @param refresh Refetches whatever the caller considers authoritative. Called
 * with no arguments and never given anything from the signal, because the
 * signal carries nothing: the point of this hook is that the server answers,
 * not the other tab.
 */
export function useOauthCompletionWatcher(
  refresh: () => Promise<unknown>,
): OauthCompletionWatcher {
  const [watching, setWatching] = useState(false);

  // Kept in a ref so a caller need not memoise its refetcher to avoid
  // resubscribing — and so the interval below always calls the latest one.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshing = useRef(false);
  const ticks = useRef(0);
  const opened = useRef<AuthorizationWindow | null>(null);

  const stop = useCallback((): void => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
    opened.current = null;
    ticks.current = 0;
    setWatching(false);
  }, []);

  /**
   * NON-OVERLAPPING. A refetch that outlives its tick would otherwise stack a
   * second, then a third, and the fallback for a slow API would be more load on
   * the same slow API.
   */
  const refreshOnce = useCallback((): void => {
    if (refreshing.current) return;
    refreshing.current = true;
    void Promise.resolve(refreshRef.current())
      .catch(() => undefined)
      .finally(() => {
        refreshing.current = false;
      });
  }, []);

  const watch = useCallback(
    (authorizationWindow: AuthorizationWindow): void => {
      stop();
      opened.current = authorizationWindow;
      ticks.current = 0;
      setWatching(true);
      timer.current = setInterval(() => {
        ticks.current += 1;
        refreshOnce();
        // A closed tab is the only thing this side can read across origins, and
        // it is enough: the provider's page is gone, so either the round trip
        // finished or the user walked away. This tick's refetch answers which.
        const abandoned = opened.current?.closed === true;
        if (abandoned || ticks.current >= OAUTH_COMPLETION_POLL_MAX_TICKS) {
          stop();
        }
      }, OAUTH_COMPLETION_POLL_INTERVAL_MS);
    },
    [refreshOnce, stop],
  );

  useEffect(() => {
    // Subscribed for the page's whole life, not just during an attempt: the
    // console may have been re-mounted, or opened in a second tab, since the
    // flow began, and a completion is worth refetching for either way. Acting
    // on it costs one refetch and can invent nothing — the signal carries no
    // claim to believe.
    const unsubscribe = subscribeToIntegrationOauthCompletion(() => {
      refreshOnce();
      stop();
    });
    return unsubscribe;
  }, [refreshOnce, stop]);

  useEffect(
    // Unmount while an attempt is in flight — the user navigated away — must
    // not leave an interval refetching for a page that is gone.
    () => () => {
      if (timer.current !== null) clearInterval(timer.current);
    },
    [],
  );

  return { watching, watch, stop };
}
